import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  channelsApi,
  type WhatsAppStatus,
} from "@/api/channels";
import type { Channel } from "@/types/channel";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Loader2,
  Smartphone,
  QrCode,
  Wifi,
  WifiOff,
  RefreshCw,
  CheckCircle2,
  Circle,
  LogOut,
} from "lucide-react";
import QRCodeLib from "qrcode";

interface WhatsAppConnectDialogProps {
  open: boolean;
  onClose: () => void;
  channel: Channel | null;
  onChannelUpdated?: () => void;
}

type Step =
  | "idle"
  | "provisioning"
  | "connecting"
  | "qr"
  | "connected"
  | "error";

const STEP_ORDER = ["provision", "connect", "qr", "done"] as const;

const STEP_LABELS: Record<(typeof STEP_ORDER)[number], string> = {
  provision: "Provisionar",
  connect: "Conectar",
  qr: "QR Code",
  done: "Concluído",
};

function getStepIndex(step: Step, needsProvision: boolean): number {
  switch (step) {
    case "idle":
      return -1;
    case "provisioning":
      return 0;
    case "connecting":
      return needsProvision ? 1 : 1;
    case "qr":
      return 2;
    case "connected":
      return 3;
    case "error":
      return -1;
    default:
      return -1;
  }
}

function StepIndicator({
  currentStep,
  needsProvision,
}: {
  currentStep: Step;
  needsProvision: boolean;
}) {
  const activeIndex = getStepIndex(currentStep, needsProvision);
  const steps = needsProvision
    ? STEP_ORDER
    : STEP_ORDER.filter((s) => s !== "provision");

  if (currentStep === "idle" || currentStep === "error") {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-1 pb-2">
      {steps.map((stepKey, i) => {
        const stepIndex = needsProvision ? i : i + 1;
        const isCompleted = activeIndex > stepIndex;
        const isActive = activeIndex === stepIndex;

        return (
          <div key={stepKey} className="flex items-center gap-1">
            {i > 0 && (
              <div
                className={cn(
                  "h-px w-6 transition-colors",
                  isCompleted || isActive ? "bg-primary" : "bg-muted",
                )}
              />
            )}
            <div className="flex flex-col items-center gap-1">
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 text-primary" />
              ) : isActive ? (
                <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary bg-primary">
                  <span className="text-[10px] font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                </div>
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground/40" />
              )}
              <span
                className={cn(
                  "text-[10px] leading-none",
                  isActive
                    ? "font-medium text-primary"
                    : isCompleted
                      ? "text-primary"
                      : "text-muted-foreground/60",
                )}
              >
                {STEP_LABELS[stepKey]}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function WhatsAppConnectDialog({
  open,
  onClose,
  channel,
  onChannelUpdated,
}: WhatsAppConnectDialogProps) {
  const [step, setStep] = useState<Step>("idle");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const skipStatusCheckRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    async (channelId: number) => {
      try {
        const res = await channelsApi.getWhatsAppStatus(channelId);
        const data: WhatsAppStatus = res.data;
        if (data.logged_in) {
          setStep("connected");
          stopPolling();
          toast.success("WhatsApp conectado!");

          // Auto-detect phone number from JID (e.g. "5511999999999:123@s.whatsapp.net")
          if (data.jid) {
            const phone = data.jid.split("@")[0].split(":")[0];
            if (phone) {
              const formatted = phone.startsWith("+") ? phone : `+${phone}`;
              try {
                await channelsApi.update(channelId, { identifier: formatted });
              } catch {
                // non-critical
              }
            }
          }

          onChannelUpdated?.();
        }
      } catch {
        // Ignore polling errors
      }
    },
    [stopPolling, onChannelUpdated],
  );

  const startPolling = useCallback(
    (channelId: number) => {
      stopPolling();
      pollingRef.current = setInterval(() => pollStatus(channelId), 3000);
    },
    [stopPolling, pollStatus],
  );

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const resetState = useCallback(() => {
    stopPolling();
    setStep("idle");
    setQrDataUrl(null);
    setError(null);
    skipStatusCheckRef.current = false;
  }, [stopPolling]);

  // Check status when dialog opens (skip if we just disconnected)
  useEffect(() => {
    if (!open || !channel) return;
    if (skipStatusCheckRef.current) return;

    let cancelled = false;

    (async () => {
      if (!channel.external_id) return;
      try {
        const res = await channelsApi.getWhatsAppStatus(channel.id);
        if (cancelled) return;
        const data: WhatsAppStatus = res.data;
        if (data.logged_in) {
          setStep("connected");
        }
      } catch {
        // ignore -- stays on idle
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, channel]);

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      resetState();
      onClose();
    }
  };

  const fetchQRImage = async (
    channelId: number,
    retries = 8,
    delayMs = 2000,
  ): Promise<string | null> => {
    for (let i = 0; i < retries; i++) {
      if (i > 0) await new Promise((r) => setTimeout(r, delayMs));
      try {
        const res = await channelsApi.getQR(channelId);
        if (res.data?.qr) {
          const dataUrl = await QRCodeLib.toDataURL(res.data.qr, {
            width: 240,
            margin: 2,
            color: { dark: "#000000", light: "#ffffff" },
          });
          return dataUrl;
        }
      } catch {
        // retry
      }
    }
    return null;
  };

  const doConnect = async (channelId: number) => {
    setStep("connecting");
    setError(null);

    try {
      await channelsApi.connect(channelId);

      // Always try to get QR code first
      const dataUrl = await fetchQRImage(channelId);
      if (dataUrl) {
        setQrDataUrl(dataUrl);
        setStep("qr");
        startPolling(channelId);
        return;
      }

      // No QR available — check if already connected
      const statusRes = await channelsApi.getWhatsAppStatus(channelId);
      if (statusRes.data?.logged_in) {
        setStep("connected");
        toast.success("WhatsApp reconectado!");
        onChannelUpdated?.();
      } else {
        setError("QR code ainda não gerado. Tente novamente.");
        setStep("error");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao conectar";
      setError(msg);
      setStep("error");
    }
  };

  const handleConnect = () => {
    if (!channel) return;
    doConnect(channel.id);
  };

  const handleRefreshQR = async () => {
    if (!channel) return;
    setStep("connecting");
    try {
      await channelsApi.connect(channel.id);
      const dataUrl = await fetchQRImage(channel.id);
      if (dataUrl) {
        setQrDataUrl(dataUrl);
        setStep("qr");
        startPolling(channel.id);
      } else {
        setError("QR code expirado. Tente novamente.");
        setStep("error");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao gerar QR code";
      setError(msg);
      setStep("error");
    }
  };

  const handleDisconnect = async () => {
    if (!channel) return;
    try {
      await channelsApi.disconnect(channel.id);
      skipStatusCheckRef.current = true;
      setStep("idle");
      toast.success("WhatsApp desconectado");
      onChannelUpdated?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao desconectar";
      toast.error(msg);
    }
  };

  const handleLogout = async () => {
    if (!channel) return;
    try {
      await channelsApi.logout(channel.id);
      skipStatusCheckRef.current = true;
      setStep("idle");
      toast.success("Sessão WhatsApp encerrada — precisará escanear QR code novamente");
      onChannelUpdated?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao deslogar";
      toast.error(msg);
    }
  };

  const needsProvision = !!(channel && !channel.external_id);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            WhatsApp -- {channel?.name || channel?.identifier}
          </DialogTitle>
        </DialogHeader>

        <StepIndicator currentStep={step} needsProvision={needsProvision} />

        <div className="flex flex-col items-center gap-4 py-4">
          {step === "connected" && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 animate-in zoom-in-50 duration-300">
                <Wifi className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-green-600">Conectado</p>
                <p className="text-xs text-muted-foreground">
                  WhatsApp conectado e recebendo mensagens
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDisconnect}>
                  <WifiOff className="mr-2 h-4 w-4" />
                  Desconectar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Deslogar
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center max-w-[280px]">
                Desconectar pausa a conexão. Deslogar encerra a sessão e exige novo QR code.
              </p>
            </div>
          )}

          {step === "idle" && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <QrCode className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">Pronto para conectar</p>
                <p className="text-xs text-muted-foreground">
                  Clique para iniciar a conexão WhatsApp via QR code.
                </p>
              </div>
              <Button onClick={handleConnect}>
                <Smartphone className="mr-2 h-4 w-4" />
                Conectar WhatsApp
              </Button>
            </div>
          )}

          {(step === "provisioning" || step === "connecting") && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center">
                <p className="text-sm font-medium">
                  {step === "provisioning"
                    ? "Provisionando..."
                    : "Gerando QR code..."}
                </p>
                <p className="text-xs text-muted-foreground">
                  {step === "provisioning"
                    ? "Criando sessão no servidor WhatsApp"
                    : "Aguarde enquanto o QR code e gerado"}
                </p>
              </div>
            </div>
          )}

          {step === "qr" && qrDataUrl && (
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-xl border-2 border-primary/20 bg-white p-4 shadow-sm">
                <img
                  src={qrDataUrl}
                  alt="WhatsApp QR Code"
                  width={240}
                  height={240}
                  className="rounded-lg"
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">Escaneie com o WhatsApp</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Abra o WhatsApp no celular, va em{" "}
                  <span className="font-medium">Dispositivos conectados</span> e
                  escaneie o codigo acima.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleRefreshQR}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Gerar novo QR
              </Button>
            </div>
          )}

          {step === "error" && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <WifiOff className="h-8 w-8 text-destructive" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-destructive">
                  Erro na conexão
                </p>
                <p className="text-xs text-destructive/80">{error}</p>
              </div>
              <Button variant="outline" onClick={handleConnect}>
                Tentar novamente
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
